import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    FormControlLabel,
    Switch,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { userAPI } from '../../../services/api';

const AddUserDialog = ({ open, onClose, branches }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        username: '',
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        branch: '',
        isActive: true
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error when field changes
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!form.username.trim()) {
            newErrors.username = t('validation.required', 'This field is required');
        }
        if (!form.name.trim()) {
            newErrors.name = t('validation.required', 'This field is required');
        }
        if (!form.password) {
            newErrors.password = t('validation.required', 'This field is required');
        } else if (form.password.length < 6) {
            newErrors.password = t('validation.minLength', 'Minimum 6 characters');
        }
        if (form.password !== form.confirmPassword) {
            newErrors.confirmPassword = t('validation.passwordMismatch', 'Passwords do not match');
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = t('validation.invalidEmail', 'Invalid email address');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                username: form.username,
                name: form.name,
                password: form.password,
                isActive: form.isActive
            };

            if (form.email) payload.email = form.email;
            if (form.branch) payload.branch = form.branch;

            await userAPI.create(payload);
            toast.success(t('users.createSuccess', 'User created successfully'));
            onClose(true);

            // Reset form
            setForm({
                username: '',
                name: '',
                email: '',
                password: '',
                confirmPassword: '',
                branch: '',
                isActive: true
            });
        } catch (err) {
            toast.error(err.response?.data?.message || t('users.createError', 'Failed to create user'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setForm({
            username: '',
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            branch: '',
            isActive: true
        });
        setErrors({});
        onClose(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('users.addTitle', 'Add New User')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.username', 'Username')}
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            error={!!errors.username}
                            helperText={errors.username}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.name', 'Name')}
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.email', 'Email')}
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.password', 'Password')}
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.confirmPassword', 'Confirm Password')}
                            name="confirmPassword"
                            type="password"
                            value={form.confirmPassword}
                            onChange={handleChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>{t('common.branch', 'Branch')}</InputLabel>
                            <Select
                                name="branch"
                                value={form.branch}
                                onChange={handleChange}
                                label={t('common.branch', 'Branch')}
                            >
                                <MenuItem value="">
                                    <em>{t('common.none', 'None')}</em>
                                </MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch._id} value={branch._id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={handleChange}
                                    name="isActive"
                                    color="success"
                                />
                            }
                            label={t('common.active', 'Active')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="outlined" disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={18} />}
                >
                    {t('common.create', 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddUserDialog;